/*
 * Video wall — a tile opens a dialog holding the clip plus a full buy panel.
 *
 * Both the <video> and the buy panel are rendered by Liquid into <template>
 * elements and only cloned when a tile is opened. Two consequences worth
 * keeping: a page view costs zero video bytes however many tiles exist, and
 * every price, rating and image in the panel is real server-side data rather
 * than something reassembled in JS.
 *
 * Add to cart inside the panel works because cart-drawer.js listens for
 * submits by delegation, so a cloned form is picked up like any other.
 *
 * Uses a native <dialog>: focus trapping, Esc and the backdrop come from the
 * platform instead of being hand-rolled.
 */
(function () {
  'use strict';

  function init(scope) {
    var root = scope || document;
    var modal = document.querySelector('[data-vwall-modal]');
    if (!modal) return;

    var stage = modal.querySelector('[data-vwall-stage]');
    var panel = modal.querySelector('[data-vwall-panel]');
    var muteBtn = modal.querySelector('[data-vwall-mute]');
    var lastOpener = null;

    function currentVideo() {
      return stage ? stage.querySelector('video') : null;
    }

    function clearStage() {
      var v = currentVideo();
      if (v) {
        v.pause();
        v.removeAttribute('src'); // stop any in-flight buffering
        v.load();
      }
      if (stage) stage.textContent = '';
      if (panel) panel.textContent = '';
    }

    function syncMuteButton() {
      if (!muteBtn) return;
      var v = currentVideo();
      var muted = !v || v.muted;
      muteBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
      var label = muteBtn.querySelector('.visually-hidden');
      if (label) label.textContent = muted ? 'Unmute video' : 'Mute video';
    }

    function open(btn) {
      lastOpener = btn;
      clearStage();

      var vt = btn.dataset.videoId ? document.getElementById(btn.dataset.videoId) : null;
      if (vt && vt.content && vt.content.childElementCount) {
        stage.appendChild(vt.content.cloneNode(true));
        var v = currentVideo();
        if (v) {
          // Muted so autoplay is actually permitted; the control below is
          // how a viewer opts into sound.
          v.muted = true;
          v.playsInline = true;
          // iOS will not start a <video> cloned out of a <template> until it
          // has been told to load: the element is new to the document and its
          // <source> children were never resolved. Desktop plays without it,
          // which is why this only showed up on a real phone.
          v.load();
          var attempt = v.play();
          if (attempt && attempt.catch) {
            attempt.catch(function () {
              // Autoplay refused: show the frame rather than a black box.
              v.controls = true;
            });
          }
        }
      } else {
        var note = document.createElement('p');
        note.className = 'vmodal__acc-body';
        note.style.cssText = 'padding:2rem;color:#fff;opacity:.8';
        note.textContent = 'Video coming soon.';
        stage.appendChild(note);
      }

      var pt = btn.dataset.panelId ? document.getElementById(btn.dataset.panelId) : null;
      if (pt && pt.content && panel) panel.appendChild(pt.content.cloneNode(true));

      syncMuteButton();

      modal.classList.remove('is-expanded');
      if (typeof modal.showModal === 'function') modal.showModal();
      else modal.setAttribute('open', '');
    }

    function close() {
      clearStage();
      if (typeof modal.close === 'function') modal.close();
      else modal.removeAttribute('open');
      if (lastOpener && lastOpener.focus) lastOpener.focus();
    }

    if (!modal.dataset.vwallReady) {
      modal.dataset.vwallReady = '1';

      modal.addEventListener('click', function (e) {
        if (e.target.closest('[data-vwall-close]')) { close(); return; }
        if (e.target === modal) { close(); return; } // backdrop
        if (e.target.closest('[data-vwall-mute]')) {
          var v = currentVideo();
          if (v) {
            v.muted = !v.muted;
            if (!v.muted) v.play().catch(function () {});
            syncMuteButton();
          }
        }
      });

      // Adding to cart opens the drawer, which would otherwise sit behind a
      // modal dialog — hand off cleanly instead.
      modal.addEventListener('submit', function () {
        setTimeout(close, 60);
      });

      // Bottom-sheet toggle. Mobile-only in effect: the control it responds
      // to is display:none above 749px, so desktop can never enter this state.
      modal.addEventListener('click', function (e) {
        if (!e.target.closest('[data-vwall-expand]')) return;
        var open = modal.classList.toggle('is-expanded');
        var label = modal.querySelector('[data-vwall-expand-label]');
        if (label) label.textContent = open ? 'Hide details' : 'Shop now';
        modal.querySelector('[data-vwall-expand]').setAttribute('aria-expanded', open ? 'true' : 'false');
      });

      // Latest five-star reviews, fetched once per product per page view when
      // the Customer reviews accordion is first opened — not on modal open,
      // so browsing videos costs no API calls.
      var junipCache = {};
      modal.addEventListener('toggle', function (e) {
        var acc = e.target;
        if (!acc.matches || !acc.matches('details') || !acc.open) return;
        var box = acc.querySelector('[data-junip-reviews]');
        if (!box || box.dataset.loaded === '1') return;
        box.dataset.loaded = '1';
        var pid = box.dataset.junipProduct, key = box.dataset.junipKey;
        if (!pid || !key) return;
        var render = function (revs) {
          box.textContent = '';
          revs.forEach(function (r) {
            var d = document.createElement('div');
            d.className = 'vmodal__review';
            var when = (r.created_at || '').slice(0, 10);
            var who = (r.customer && (r.customer.first_name || r.customer.name)) || 'Verified buyer';
            d.innerHTML = '<div class="vmodal__review-head">' +
              '<span class="vmodal__review-stars" aria-hidden="true">★★★★★</span>' +
              '<span class="vmodal__review-meta"></span></div>' +
              '<p class="vmodal__review-title"></p><p class="vmodal__review-body"></p>';
            d.querySelector('.vmodal__review-meta').textContent = who + (when ? ' · ' + when : '');
            d.querySelector('.vmodal__review-title').textContent = r.title || '';
            d.querySelector('.vmodal__review-body').textContent = r.body || '';
            box.appendChild(d);
          });
        };
        if (junipCache[pid]) { render(junipCache[pid]); return; }
        fetch('https://api.juniphq.com/v1/product_reviews?filter[product_remote_ids]=' + pid +
              '&filter[rating]=5&page[size]=5&include=customer', {
          headers: { 'Junip-Store-Key': key }
        }).then(function (r) { return r.ok ? r.json() : Promise.reject(); })
          .then(function (d) {
            var revs = (d.product_reviews || []).slice(0, 5);
            junipCache[pid] = revs;
            render(revs);
          })
          .catch(function () { box.dataset.loaded = ''; }); // aggregate line still stands
      }, true);

      modal.addEventListener('close', clearStage);
      modal.addEventListener('cancel', clearStage); // Esc

      // Swipe-down closes, phones only. Passive observers, nothing is
      // prevented: the collapsed view has nothing scrollable, so a clearly
      // vertical downward drag can only mean "dismiss". Ignored while the
      // sheet is expanded — there the drag belongs to the panel's scroll —
      // and any touch that starts inside the panel belongs to the panel.
      var swipe = null;
      modal.addEventListener('touchstart', function (e) {
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        if (modal.classList.contains('is-expanded')) return;
        if (e.target.closest('[data-vwall-panel], .vmodal__panel')) return;
        swipe = { y: e.touches[0].clientY, x: e.touches[0].clientX };
      }, { passive: true });
      modal.addEventListener('touchmove', function (e) {
        if (!swipe) return;
        var dy = e.touches[0].clientY - swipe.y;
        var dx = Math.abs(e.touches[0].clientX - swipe.x);
        if (dy > 90 && dy > dx * 2) { swipe = null; close(); }
      }, { passive: true });
      modal.addEventListener('touchend', function () { swipe = null; }, { passive: true });
    }

    // ---- looping tile previews -------------------------------------------
    // Sources are withheld until a tile is actually on screen, so a page view
    // costs nothing; each preview then loops only its opening seconds.
    root.querySelectorAll('[data-vwall-root]').forEach(function (wall) {
      if (wall.dataset.previewsReady === '1') return;
      wall.dataset.previewsReady = '1';

      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var conn = navigator.connection || {};
      if (reduced || conn.saveData) return; // posters already carry the tiles

      var seconds = Number(wall.dataset.previewSeconds) || 4;
      var previews = [].slice.call(wall.querySelectorAll('[data-vwall-preview]'));
      if (!previews.length || !('IntersectionObserver' in window)) return;

      function loopWindow(v) {
        // `loop` alone would replay the WHOLE clip; this keeps it to the hook.
        if (v.currentTime > seconds) v.currentTime = 0;
      }

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (entry.isIntersecting) {
            if (!v.getAttribute('src') && v.dataset.src) {
              v.setAttribute('src', v.dataset.src);
              v.addEventListener('timeupdate', function () { loopWindow(v); });
            }
            v.play().catch(function () {});
          } else {
            v.pause();
          }
        });
      }, { rootMargin: '150px' });

      previews.forEach(function (v) { io.observe(v); });

      // A hidden tab should not be decoding twelve videos.
      document.addEventListener('visibilitychange', function () {
        previews.forEach(function (v) {
          if (document.hidden) v.pause();
          else if (v.getAttribute('src')) v.play().catch(function () {});
        });
      });
    });

    root.querySelectorAll('[data-vwall-open]').forEach(function (btn) {
      if (btn.dataset.vwallBound === '1') return;
      btn.dataset.vwallBound = '1';
      btn.addEventListener('click', function () { open(btn); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
